import React, { useState } from 'react';
import MapView from './MapView';
import HotelSearch from './HotelSearch';
import FlightSearch from './FlightSearch';
import axios from 'axios';
import { 
  Box, 
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Slider,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  Fade,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Divider,
  Stack,
  OutlinedInput,
  FormHelperText,
  ListSubheader,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  MobileStepper
} from '@mui/material';
import {
  FlightTakeoff,
  Schedule,
  LocationOn,
  DirectionsCar,
  Restaurant,
  Hotel,
  Park,
  Museum,
  LocalCafe,
  AttachMoney,
  Refresh,
  RefreshIcon,
  Close as CloseIcon,
  Map as MapIcon,
  DirectionsWalk,
  DirectionsTransit,
  PedalBike,
  Tour,
  LocalTaxi,
  TwoWheeler,
  LocalTaxi as RideshareIcon,
  Shuffle as MixedIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import Weather from './Weather';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000';

const HeroText = styled(Typography)(({ theme }) => ({
  fontSize: '4rem',
  fontWeight: 800,
  lineHeight: 1.1,
  marginBottom: theme.spacing(2),
  background: 'linear-gradient(180deg, #000 0%, rgba(0, 0, 0, 0.9) 90%, rgba(0, 0, 0, 0.7) 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  animation: 'fadeIn 0.6s ease-out forwards',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '4rem',
    lineHeight: 1.2,
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '5rem',
  },
}));

const SubText = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  color: 'rgba(0, 0, 0, 0.7)',
  marginBottom: theme.spacing(4),
  maxWidth: '32rem',
  lineHeight: 1.5,
  fontWeight: 500,
  opacity: 0,
  animation: 'fadeIn 0.6s ease-out forwards',
  animationDelay: '150ms',
  textAlign: 'center',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: '0 16px',
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '24px',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s ease',
  overflow: 'visible',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.12)',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '16px',
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '100px',
  padding: '12px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
  background: 'linear-gradient(45deg, #2196F3, #21CBF3)',
  color: 'white',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,118,255,0.39)',
  }
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8),
  }
}));

const AnimatedBox = styled(Box)(({ theme }) => ({
  opacity: 0,
  animation: 'fadeIn 0.8s ease-out forwards',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '20px',
  height: '100%',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  }
}));

const InterestChip = styled(Chip)(({ theme, selected }) => ({
  borderRadius: '100px',
  padding: '8px 4px',
  height: 'auto',
  transition: 'all 0.2s ease',
  background: selected ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.04)',
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    background: selected ? theme.palette.primary.dark : 'rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
    background: 'rgba(0, 0, 0, 0.04)',
  },
  '& .MuiChip-label': {
    padding: '4px 12px',
    fontSize: '0.9rem',
  }
}));

const AnimatedCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  }
}));

const ActivityCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(0, 0, 0, 0.04)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const StyledTimeline = styled(Timeline)(({ theme }) => ({
  '.MuiTimelineItem-root': {
    '&:before': {
      flex: 0,
      padding: 0,
    }
  },
  '.MuiTimelineDot-root': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'scale(1.1)',
    }
  },
  '.MuiTimelineConnector-root': {
    backgroundColor: theme.palette.primary.light,
    width: 2,
  },
  '.MuiTimelineContent-root': {
    padding: '12px 16px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
      borderRadius: '12px',
    }
  },
  '.MuiTimelineOppositeContent-root': {
    flex: 0.2,
  }
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
    }
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
    }
  }
}));

const StyledSlider = styled(Slider)(({ theme }) => ({
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 0 0 8px rgba(33, 150, 243, 0.16)',
    }
  },
  '& .MuiSlider-track': {
    height: 4,
  },
  '& .MuiSlider-rail': {
    height: 4,
    opacity: 0.2,
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#bfbfbf',
    height: 8,
    width: 2,
    marginTop: -2,
  },
  '& .MuiSlider-markActive': {
    opacity: 1,
    backgroundColor: 'currentColor',
  }
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  borderRadius: '12px',
  padding: '12px 20px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  backdropFilter: 'blur(8px)',
  '& .MuiAlert-icon': {
    color: theme.palette.primary.main
  },
  '& .MuiAlert-message': {
    fontWeight: 500
  },
  '&.MuiAlert-standardError': {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
    color: theme.palette.error.dark
  },
  '&.MuiAlert-standardSuccess': {
    backgroundColor: alpha(theme.palette.success.main, 0.1),
    color: theme.palette.success.dark
  },
  '&.MuiAlert-standardInfo': {
    backgroundColor: alpha(theme.palette.info.main, 0.1),
    color: theme.palette.info.dark
  },
  '&.MuiAlert-standardWarning': {
    backgroundColor: alpha(theme.palette.warning.main, 0.1),
    color: theme.palette.warning.dark
  }
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  '.MuiStepLabel-root': {
    padding: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5),
    }
  },
  '.MuiStepLabel-label': {
    fontWeight: 500,
    transition: 'all 0.2s ease',
    marginLeft: theme.spacing(2),
    '&.Mui-active': {
      color: theme.palette.primary.main,
      transform: 'scale(1.05)',
      fontWeight: 600
    },
    '&.Mui-completed': {
      color: theme.palette.success.main,
    }
  },
  '.MuiStepIcon-root': {
    width: '32px',
    height: '32px',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '24px',
      height: '24px',
    },
    transition: 'all 0.3s ease',
    '&.Mui-active': {
      color: theme.palette.primary.main,
      transform: 'scale(1.2)',
    },
    '&.Mui-completed': {
      color: theme.palette.success.main,
    }
  },
  '.MuiStepContent-root': {
    borderLeft: `2px solid ${theme.palette.primary.light}`,
    marginLeft: '16px',
    paddingLeft: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginLeft: '12px',
      paddingLeft: theme.spacing(3),
    }
  }
}));

const StepContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.1)',
  }
}));

const StepNavigationButton = styled(Button)(({ theme }) => ({
  borderRadius: '100px',
  padding: '12px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
  background: 'linear-gradient(45deg, #2196F3, #21CBF3)',
  color: 'white',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    padding: '10px 24px',
    fontSize: '1rem',
    width: '100%',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,118,255,0.39)',
  },
  '&:disabled': {
    background: theme.palette.grey[300],
    boxShadow: 'none',
  }
}));

const TRAVEL_MODES = [
  { value: 'walking', label: 'Walking', icon: <DirectionsWalk /> },
  { value: 'public transport', label: 'Public Transport', icon: <DirectionsTransit /> },
  { value: 'rental car', label: 'Rental Car', icon: <DirectionsCar /> },
  { value: 'bike', label: 'Bike', icon: <PedalBike /> },
  { value: 'guided tours', label: 'Guided Tours', icon: <Tour /> },
  { value: 'private car', label: 'Private Car', icon: <DirectionsCar /> },
  { value: 'scooter', label: 'Scooter', icon: <TwoWheeler /> },
  { value: 'rideshare', label: 'Rideshare', icon: <RideshareIcon /> },
  { value: 'mixed', label: 'Mixed', icon: <MixedIcon /> }
];

const getActivityIcon = (activity) => {
  if (activity.toLowerCase().includes('breakfast') || activity.toLowerCase().includes('lunch') || activity.toLowerCase().includes('dinner')) {
    return <Restaurant />;
  } else if (activity.toLowerCase().includes('hotel')) {
    return <Hotel />;
  } else if (activity.toLowerCase().includes('hike') || activity.toLowerCase().includes('park')) {
    return <Park />;
  } else if (activity.toLowerCase().includes('museum')) {
    return <Museum />;
  } else if (activity.toLowerCase().includes('café') || activity.toLowerCase().includes('coffee')) {
    return <LocalCafe />;
  }
  return <LocationOn />;
};

const VALID_INTERESTS = [
  // Culture & Arts
  "art", "theater", "museums", "history", "architecture", "cultural events",
  // Nature & Outdoors
  "hiking", "wildlife", "beaches", "national parks", "adventure sports",
  // Food & Drink
  "cuisine", "street food", "wine tasting", "breweries", "fine dining",
  // Wellness
  "spa", "yoga retreats", "relaxation", "resorts",
  // Shopping
  "shopping", "luxury brands", "local markets",
  // Family & Entertainment
  "theme parks", "zoos", "kid-friendly activities",
  // Nightlife & Entertainment
  "bars", "clubs", "live music", "theater shows",
  // Sports & Activities
  "sports events", "fitness", "cycling",
  // Technology & Business
  "tech", "innovation", "conventions",
  // Photography
  "photography", "scenic views"
];

const INTEREST_CATEGORIES = {
  "Culture & Arts": ["art", "theater", "museums", "history", "architecture", "cultural events"],
  "Nature & Outdoors": ["hiking", "wildlife", "beaches", "national parks", "adventure sports"],
  "Food & Drink": ["cuisine", "street food", "wine tasting", "breweries", "fine dining"],
  "Wellness": ["spa", "yoga retreats", "relaxation", "resorts"],
  "Shopping": ["shopping", "luxury brands", "local markets"],
  "Family & Entertainment": ["theme parks", "zoos", "kid-friendly activities"],
  "Nightlife": ["bars", "clubs", "live music", "theater shows"],
  "Sports & Activities": ["sports events", "fitness", "cycling"],
  "Technology": ["tech", "innovation", "conventions"],
  "Photography": ["photography", "scenic views"]
};

const BUDGET_OPTIONS = [
  { value: 'low', label: 'Budget Friendly', icon: <AttachMoney /> },
  { value: 'medium', label: 'Moderate', icon: <Box sx={{ display: 'flex' }}><AttachMoney /><AttachMoney /></Box> },
  { value: 'high', label: 'Luxury', icon: <Box sx={{ display: 'flex' }}><AttachMoney /><AttachMoney /><AttachMoney /></Box> }
];

const PLANNING_MODES = [
  { 
    value: 'complete', 
    label: 'Plan Complete Trip',
    description: 'Plan everything including flights, hotels, and activities',
    icon: <FlightTakeoff />
  },
  { 
    value: 'activities', 
    label: 'Plan Activities Only',
    description: 'Plan activities for a trip you\'ve already booked',
    icon: <LocationOn />
  }
];

// Add keyframe animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeIn {
    0% { opacity: 0; transform: translateY(12px); }
    100% { opacity: 1; transform: translateY(0); }
  }
`;
document.head.appendChild(styleSheet);

const TripPlanner = () => {
  const [planningMode, setPlanningMode] = useState('activities');
  const [destination, setDestination] = useState('');
  const [dates, setDates] = useState({
    arrival: '',
    departure: ''
  });
  const [guests, setGuests] = useState({
    adults: 1,
    children: 0,
    childrenAges: [],
    rooms: 1
  });
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showHotelSearch, setShowHotelSearch] = useState(false);
  const [showFlightSearch, setShowFlightSearch] = useState(false);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [formData, setFormData] = useState({
    planningMode: 'activities',
    days: 3,
    destination: '',
    interests: [],
    budget: 'medium',
    travelMode: 'public transport',
    startDate: null,
    departureCity: '',
    returnDate: null,
    adults: 1,
    children: 0,
    rooms: 1,
    zip_code: '',
    contextPrompt: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tripPlan, setTripPlan] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [adjustmentPrompt, setAdjustmentPrompt] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);
  const [budgetAmount, setBudgetAmount] = useState(0);
  const [totalCosts, setTotalCosts] = useState({
    hotel: 0,
    flight: 0,
    activities: 0
  });
  const [dayAdjustments, setDayAdjustments] = useState({});
  const [adjustingDay, setAdjustingDay] = useState(null);
  const [dayErrors, setDayErrors] = useState({});
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      label: 'Basic Details',
      description: 'Where and when do you want to go?',
      optional: false,
      validate: () => {
    if (!formData.destination.trim()) {
      setError('Please enter a destination');
      return false;
    }
        if (!dates.arrival || !dates.departure) {
          setError('Please select your travel dates');
          return false;
        }
        return true;
      }
    },
    {
      label: 'Trip Style',
      description: 'What kind of trip are you planning?',
      optional: false,
      validate: () => {
        if (!formData.budget) {
          setError('Please select your budget preference');
          return false;
        }
        return true;
      }
    },
    {
      label: 'Interests & Activities',
      description: 'What would you like to do?',
      optional: false,
      validate: () => {
    if (formData.interests.length === 0) {
      setError('Please select at least one interest');
      return false;
    }
    return true;
      }
    },
    {
      label: 'Travel Details',
      description: 'How would you like to get around?',
      optional: true,
      validate: () => true
    },
    {
      label: 'Personal Context',
      description: 'Tell us more about your specific needs and preferences',
      optional: true,
      validate: () => true
    }
  ];

  const handleNext = () => {
    if (steps[activeStep].validate()) {
      setActiveStep((prevStep) => prevStep + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError(null);
  };

  const handleStepSubmit = (e) => {
    e.preventDefault();
    if (activeStep === steps.length - 1) {
      handleSubmit(e);
    } else {
      handleNext();
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Where do you want to go?"
                value={formData.destination}
                onChange={(e) => {
                  const newDest = e.target.value;
                  setFormData({...formData, destination: newDest});
                  setDestination(newDest);
                }}
                required
                placeholder="e.g., Barcelona, Tokyo, New York..."
                InputProps={{
                  startAdornment: <LocationOn sx={{ color: 'primary.main', mr: 1 }} />
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Zip Code (Optional)"
                value={formData.zip_code}
                onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                placeholder="Enter zip code for more localized recommendations"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Arrival Date"
                type="date"
                value={dates.arrival}
                onChange={(e) => setDates({ ...dates, arrival: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Departure Date"
                type="date"
                value={dates.departure}
                onChange={(e) => setDates({ ...dates, departure: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                What's your travel style?
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                flexWrap: 'wrap',
                mt: 2 
              }}>
                {BUDGET_OPTIONS.map((option) => (
                  <StyledCard
                    key={option.value}
                    onClick={() => setFormData({...formData, budget: option.value})}
                    sx={{
                      cursor: 'pointer',
                      flex: 1,
                      minWidth: 200,
                      bgcolor: formData.budget === option.value ? 'primary.light' : 'background.paper',
                      color: formData.budget === option.value ? 'primary.contrastText' : 'text.primary',
                    }}
                  >
                    <CardContent sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      {option.icon}
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {option.label}
                      </Typography>
                    </CardContent>
                  </StyledCard>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                How long is your trip?
              </Typography>
              <StyledSlider
                value={formData.days}
                onChange={(e, newValue) => setFormData({...formData, days: newValue})}
                min={1}
                max={14}
                marks
                valueLabelDisplay="auto"
                sx={{ mt: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                What's your total budget?
              </Typography>
              <StyledTextField
                fullWidth
                type="number"
                label="Total Budget (USD)"
                value={budgetAmount || ''}
                onChange={(e) => setBudgetAmount(e.target.value === '' ? 0 : Math.max(0, parseFloat(e.target.value)))}
                InputProps={{
                  startAdornment: <AttachMoney />,
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                What interests you?
              </Typography>
              <Box sx={{ mt: 2 }}>
                {Object.entries(INTEREST_CATEGORIES).map(([category, interests]) => (
                  <Box key={category} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.secondary' }}>
                      {category}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {interests.map((interest) => (
                        <InterestChip
                          key={interest}
                          label={interest}
                          onClick={() => {
                            const newInterests = formData.interests.includes(interest)
                              ? formData.interests.filter(i => i !== interest)
                              : formData.interests.length < 3
                                ? [...formData.interests, interest]
                                : formData.interests;
                            setFormData({...formData, interests: newInterests});
                          }}
                          selected={formData.interests.includes(interest)}
                          disabled={formData.interests.length >= 3 && !formData.interests.includes(interest)}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                How would you like to get around?
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                flexWrap: 'wrap',
                mt: 2 
              }}>
                {TRAVEL_MODES.map((mode) => (
                  <StyledCard
                    key={mode.value}
                    onClick={() => setFormData({...formData, travelMode: mode.value})}
                    sx={{
                      cursor: 'pointer',
                      flex: '1 0 150px',
                      maxWidth: '200px',
                      bgcolor: formData.travelMode === mode.value ? 'primary.light' : 'background.paper',
                      color: formData.travelMode === mode.value ? 'primary.contrastText' : 'text.primary',
                    }}
                  >
                    <CardContent sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                    }}>
                      {mode.icon}
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {mode.label}
                      </Typography>
                    </CardContent>
                  </StyledCard>
                ))}
              </Box>
            </Grid>
            {planningMode === 'complete' && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <StepNavigationButton
                      variant="contained"
                      onClick={() => setShowHotelSearch(true)}
                      startIcon={<Hotel />}
                    >
                      Search Hotels
                    </StepNavigationButton>
                    <StepNavigationButton
                      variant="contained"
                      onClick={() => setShowFlightSearch(true)}
                      startIcon={<FlightTakeoff />}
                    >
                      Search Flights
                    </StepNavigationButton>
                  </Box>
                </Grid>
                {(selectedHotel || selectedFlight) && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2 }}>
                      {selectedHotel && (
                        <StyledCard sx={{ mb: 2 }}>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Selected Hotel: {selectedHotel.property.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              ${selectedHotel.property.priceBreakdown.grossPrice.value} per night
                            </Typography>
                          </CardContent>
                        </StyledCard>
                      )}
                      {selectedFlight && (
                        <StyledCard>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom>
                              Selected Flight
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {selectedFlight.price.formatted}
                            </Typography>
                          </CardContent>
                        </StyledCard>
                      )}
                    </Box>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Any specific preferences or requirements?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Tell us about your travel style, dietary restrictions, accessibility needs, or any other preferences.
              </Typography>
              <StyledTextField
                fullWidth
                multiline
                rows={4}
                value={formData.contextPrompt}
                onChange={(e) => setFormData({ ...formData, contextPrompt: e.target.value })}
                placeholder="Examples:
• I'm traveling solo and prefer quiet, local spots
• I'm vegetarian and love trying local plant-based cuisine
• We're bringing our 5-year-old and need kid-friendly activities
• I have mobility issues and need accessible locations
• I prefer a relaxed pace with plenty of downtime
• I want to avoid tourist traps and explore local culture"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  }
                }}
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  const validateForm = () => {
    if (!formData.destination.trim()) {
      setError('Please enter a destination');
      return false;
    }
    if (formData.interests.length === 0) {
      setError('Please select at least one interest');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
        return;
    }

    setLoading(true);
    setTripPlan(null);
    setLoadingProgress({ current: 0, total: formData.days });
    setIsFullyLoaded(false);
    setError(null);

    try {
        // Use fetch for streaming support
      const response = await fetch(`${BACKEND_API_URL}/generate-plan`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
        body: JSON.stringify({
          ...formData,
          dates: {
            arrival: dates.arrival,
            departure: dates.departure
          },
          guests,
          context_prompt: formData.contextPrompt
        })
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let firstDayReceived = false;

        while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
                // Process any remaining data in buffer
                if (buffer) {
                    try {
                        const finalDay = JSON.parse(buffer);
                        setTripPlan(current => {
                            if (!current) return finalDay;
                            return {
                                ...finalDay,
                                plan: {
                                    ...finalDay.plan,
                                    itinerary: [...current.plan.itinerary, ...finalDay.plan.itinerary]
                                }
                            };
                        });
                    } catch (e) {
                        console.error('Error parsing final buffer:', e);
                    }
                }
                setIsFullyLoaded(true);
                break;
            }

            // Decode the chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });

            // Split by newline and process complete JSON objects
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep the last incomplete line in buffer

            for (const line of lines) {
                if (line.trim()) {
                    try {
                        const dayPlan = JSON.parse(line);
                        
                        setTripPlan(current => {
                            if (!current) {
                                // First day received
                                if (!firstDayReceived) {
                                    firstDayReceived = true;
                                    // Scroll to results after first day is loaded
                                    window.scrollTo({
                                        top: document.querySelector('#tripResults')?.offsetTop,
                                        behavior: 'smooth'
                                    });
                                }
                                return dayPlan;
                            }

                            // Merge the new day with existing plan
                            return {
                                ...dayPlan,
                                plan: {
                                    ...dayPlan.plan,
                                    itinerary: [...current.plan.itinerary, ...dayPlan.plan.itinerary]
                                }
                            };
                        });

                        // Update progress
                        setLoadingProgress(prev => ({
                            ...prev,
                            current: Math.min(prev.current + 1, prev.total)
                        }));

                    } catch (e) {
                        console.error('Error parsing day plan:', e);
                    }
                }
            }
        }
    } catch (err) {
        setError(err.message || 'Failed to generate trip plan. Please try again.');
    } finally {
        setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      planningMode: 'activities',
      days: 3,
      destination: '',
      interests: [],
      budget: 'medium',
      travelMode: 'public transport',
      startDate: null,
      departureCity: '',
      returnDate: null,
      adults: 1,
      children: 0,
      rooms: 1,
      zip_code: '',
      contextPrompt: ''
    });
    setError(null);
    setTripPlan(null);
    setSubmitAttempted(false);
  };

  const handleOpenMap = () => setMapOpen(true);
  const handleCloseMap = () => setMapOpen(false);

  const getActivitiesForMap = () => {
    if (!tripPlan?.plan?.itinerary) {
      return [];
    }
    
    return tripPlan.plan.itinerary.flatMap(day => 
      day.activities.map(activity => ({
        ...activity,
        day: day.day
      }))
    ).filter(item => 
      item.latitude && 
      item.longitude && 
      !isNaN(parseFloat(item.latitude)) && 
      !isNaN(parseFloat(item.longitude))
    );
  };

  const handleAdjustPlan = async () => {
    if (!adjustmentPrompt.trim()) return;
    
    setAdjusting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${BACKEND_API_URL}/adjust-plan`, {
        currentPlan: tripPlan,
        prompt: adjustmentPrompt
      });
      
      setTripPlan(response.data);
      setAdjustmentPrompt('');
    } catch (err) {
      setError('Failed to adjust plan: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAdjusting(false);
    }
  };

  const handleHotelSelect = (hotel) => {
    setSelectedHotel(hotel);
    setShowHotelSearch(false);
    // Calculate total hotel cost for the entire stay
    const nights = Math.ceil((new Date(dates.departure) - new Date(dates.arrival)) / (1000 * 60 * 60 * 24));
    const hotelCost = hotel.property.priceBreakdown.grossPrice.value * nights;
    setTotalCosts(prev => ({ ...prev, hotel: hotelCost }));
  };

  const handleFlightSelect = (flight) => {
    setSelectedFlight(flight);
    setShowFlightSearch(false);
    // Extract flight cost - handle both price formats
    let flightCost = 0;
    if (flight.price) {
      if (typeof flight.price.amount === 'number') {
        flightCost = flight.price.amount;
      } else if (flight.price.formatted) {
        // Remove currency symbol and commas, then parse
        flightCost = parseFloat(flight.price.formatted.replace(/[^0-9.]/g, ''));
      }
    }
    setTotalCosts(prev => ({ ...prev, flight: flightCost }));
  };

  // Add effect to track activity costs
  React.useEffect(() => {
    if (tripPlan?.plan?.itinerary) {
      const activityCosts = tripPlan.plan.itinerary.reduce((total, day) => {
        return total + day.activities.reduce((dayTotal, activity) => {
          const cost = activity.estimated_cost ? parseFloat(activity.estimated_cost.replace(/[^0-9.-]+/g, '')) : 0;
          return dayTotal + (isNaN(cost) ? 0 : cost);
        }, 0);
      }, 0);

      // Only include hotel/flight costs if not in activities-only mode
      if (formData.planningMode === 'activities') {
        setTotalCosts({ hotel: 0, flight: 0, activities: activityCosts });
      } else {
        setTotalCosts(prev => ({ ...prev, activities: activityCosts }));
      }
    }
  }, [tripPlan, formData.planningMode]);

  // Add budget calculation helper
  const calculateBudgetStatus = () => {
    // Ensure all values are numbers and default to 0 if NaN
    const hotelCost = isNaN(totalCosts.hotel) ? 0 : totalCosts.hotel;
    const flightCost = isNaN(totalCosts.flight) ? 0 : totalCosts.flight;
    const activitiesCost = isNaN(totalCosts.activities) ? 0 : totalCosts.activities;
    
    const total = hotelCost + flightCost + activitiesCost;
    const remaining = budgetAmount - total;
    const percentage = budgetAmount > 0 ? (total / budgetAmount) * 100 : 0;
    
    return {
      total,
      remaining,
      percentage,
      isOverBudget: total > budgetAmount
    };
  };

  // Add budget status component
  const BudgetStatus = () => {
    const { total, remaining, percentage, isOverBudget } = calculateBudgetStatus();
    
    return (
      <Box sx={{ mt: 3, p: 3, borderRadius: 2, bgcolor: 'background.paper', boxShadow: 1 }}>
        <Typography variant="h6" gutterBottom>Budget Overview</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography color="text.secondary">Total Budget</Typography>
              <Typography variant="h5">${budgetAmount.toLocaleString()}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography color="text.secondary">Total Spent</Typography>
              <Typography variant="h5" color={isOverBudget ? 'error.main' : 'success.main'}>
                ${total.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {isOverBudget ? 'Over Budget' : 'Remaining'}
            </Typography>
            <Typography variant="body2" color={isOverBudget ? 'error.main' : 'success.main'}>
              ${Math.abs(remaining).toLocaleString()}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(percentage, 100)}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                bgcolor: isOverBudget ? 'error.main' : percentage > 80 ? 'warning.main' : 'success.main',
              }
            }}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Cost Breakdown</Typography>
          <Grid container spacing={1}>
            {totalCosts.hotel > 0 && (
              <Grid item xs={4}>
                <Chip
                  icon={<Hotel />}
                  label={`Hotel: $${totalCosts.hotel.toLocaleString()}`}
                  variant="outlined"
                  sx={{ width: '100%' }}
                />
              </Grid>
            )}
            {(totalCosts.flight > 0 || selectedFlight) && (
              <Grid item xs={4}>
                <Chip
                  icon={<FlightTakeoff />}
                  label={`Flight: $${totalCosts.flight.toLocaleString()}`}
                  variant="outlined"
                  sx={{ width: '100%' }}
                />
              </Grid>
            )}
            {totalCosts.activities > 0 && (
              <Grid item xs={4}>
                <Chip
                  icon={<LocationOn />}
                  label={`Activities: $${totalCosts.activities.toLocaleString()}`}
                  variant="outlined"
                  sx={{ width: '100%' }}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    );
  };

  const handleAdjustDay = async (dayNumber, prompt) => {
    if (!prompt.trim()) return;
    
    setAdjustingDay(dayNumber);
    setDayErrors(prev => ({ ...prev, [dayNumber]: null }));
    
    try {
      // Create a temporary plan with just this day
      const dayToAdjust = tripPlan.plan.itinerary.find(d => d.day === dayNumber);
      const tempPlan = {
        ...tripPlan,
        plan: {
          ...tripPlan.plan,
          itinerary: [dayToAdjust]
        }
      };

      const response = await axios.post(`${BACKEND_API_URL}/adjust-plan`, {
        currentPlan: tempPlan,
        prompt,
        process_single_day: true
      });
      
      // Update the trip plan with the adjusted day
      setTripPlan(current => ({
        ...current,
        plan: {
          ...current.plan,
          itinerary: current.plan.itinerary.map(day => 
            day.day === dayNumber ? response.data.plan.itinerary[0] : day
          )
        }
      }));
      
      // Clear the adjustment prompt and error for this day
      setDayAdjustments(prev => ({
        ...prev,
        [dayNumber]: ''
      }));
      setDayErrors(prev => ({ ...prev, [dayNumber]: null }));
    } catch (err) {
      setDayErrors(prev => ({ 
        ...prev, 
        [dayNumber]: 'Failed to adjust day: ' + (err.response?.data?.detail || err.message)
      }));
    } finally {
      setAdjustingDay(null);
    }
  };

  const renderTripPlan = () => {
    if (!tripPlan) return null;
    const plan = tripPlan.plan;

      return (
      <Box id="tripResults">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          background: 'rgba(255, 255, 255, 0.8)',
          p: 3,
          borderRadius: '16px',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}>
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 600,
              background: 'linear-gradient(45deg, #2196F3, #21CBF3)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Your Trip to {plan.destination}
            </Typography>
            {!isFullyLoaded && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="subtitle1" color="text.secondary">
                    Loading Day {loadingProgress.current} of {loadingProgress.total}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(loadingProgress.current / loadingProgress.total) * 100}
                  sx={{ 
                    mt: 1,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                    }
                  }} 
                />
              </Box>
            )}
          </Box>
          <ActionButton 
            variant="contained"
            startIcon={<MapIcon />}
            onClick={handleOpenMap}
          >
            View on Map
          </ActionButton>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ 
          mb: 3,
          fontWeight: 600,
          color: theme => theme.palette.primary.main
        }}>
          Daily Itinerary
        </Typography>

        {plan.itinerary.map((day, index) => (
          <Fade 
            key={index} 
            in={true} 
            timeout={300} 
            style={{ transitionDelay: `${index * 150}ms` }}
          >
            <ActivityCard>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                background: theme => alpha(theme.palette.primary.main, 0.05)
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                    Day {day.day}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Examples: 'Add more outdoor activities', 'Include local food spots', 'Make it more budget-friendly'" placement="top">
                      <StyledTextField
                        size="small"
                        placeholder="How would you like to adjust this day?"
                        value={dayAdjustments[day.day] || ''}
                        onChange={(e) => setDayAdjustments(prev => ({
                          ...prev,
                          [day.day]: e.target.value
                        }))}
                        sx={{ minWidth: 300 }}
                        error={Boolean(dayErrors[day.day])}
                        helperText={dayErrors[day.day] || "Be specific about what you'd like to change"}
                      />
                    </Tooltip>
                    <Tooltip title="Update this day's activities based on your preferences">
                      <span>
                        <ActionButton
                          variant="contained"
                          size="small"
                          onClick={() => handleAdjustDay(day.day, dayAdjustments[day.day])}
                          disabled={adjustingDay === day.day || !dayAdjustments[day.day]?.trim()}
                        >
                          {adjustingDay === day.day ? <CircularProgress size={20} /> : 'Adjust Day'}
                        </ActionButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ p: 2 }}>
                <StyledTimeline>
                {Array.isArray(day.activities) ? (
                  day.activities.map((activity, actIndex) => (
                    <TimelineItem key={actIndex}>
                        <TimelineOppositeContent color="text.secondary" sx={{ fontWeight: 500 }}>
                        {activity.time}
                      </TimelineOppositeContent>
                      <TimelineSeparator>
                        <TimelineDot color="primary">
                          {getActivityIcon(activity.activity)}
                        </TimelineDot>
                        {actIndex < day.activities.length - 1 && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent>
                        <Box component="div">
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {activity.business_name || activity.activity}
                            {activity.business_name && activity.business_name !== activity.activity && (
                              <Typography variant="body2" sx={{ 
                                color: 'text.secondary',
                                mt: 0.5
                              }}>
                                {activity.activity}
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                        <Box component="div" sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.5,
                          mt: 0.5,
                          fontSize: '0.9rem',
                          lineHeight: 1.4,
                          color: 'text.secondary'
                        }}>
                          <LocationOn sx={{ fontSize: 'small', mt: 0.3 }} />
                          <Box component="div" sx={{ flex: 1 }}>
                            {activity.location}
                            {(activity.business_type || activity.rating || activity.website || activity.phone_number || activity.opening_status) && (
                              <Box component="div" sx={{ 
                                display: 'block', 
                                fontSize: '0.85rem', 
                                color: 'rgba(0,0,0,0.6)',
                                marginTop: '2px'
                              }}>
                                {activity.business_type}
                                {activity.rating && ` • ${parseFloat(activity.rating).toFixed(1)}★`}
                                {activity.review_count && ` (${activity.review_count} reviews)`}
                                {activity.opening_status && ` • ${activity.opening_status}`}
                                {activity.phone_number && (
                                  <>
                                    {' • '}
                                    <a 
                                      href={`tel:${activity.phone_number}`}
                                      style={{ 
                                        color: '#2196F3',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      {activity.phone_number}
                                    </a>
                                  </>
                                )}
                                {activity.website && (
                                  <>
                                    {' • '}
                                    <a 
                                      href={activity.website}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ 
                                        color: '#2196F3',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      Visit Website
                                    </a>
                                  </>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </TimelineContent>
                    </TimelineItem>
                  ))
                ) : (
                  <Typography color="error">No activities found for this day</Typography>
                )}
                </StyledTimeline>
              </Box>
            </CardContent>
          </ActivityCard>
          </Fade>
        ))}

        {tripPlan && (
          <Box sx={{ 
            mt: 4,
            p: 3,
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
          }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Adjust Your Plan
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <StyledTextField
                fullWidth
                multiline
                rows={2}
                value={adjustmentPrompt}
                onChange={(e) => setAdjustmentPrompt(e.target.value)}
                placeholder="Example: I want more Mexican food options and tech-focused activities..."
                variant="outlined"
              />
              <ActionButton
                variant="contained"
                onClick={handleAdjustPlan}
                disabled={adjusting || !adjustmentPrompt.trim()}
                sx={{ minWidth: 120 }}
              >
                {adjusting ? <CircularProgress size={24} /> : 'Adjust Plan'}
              </ActionButton>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <StyledContainer maxWidth="lg">
      <AnimatedBox sx={{ mb: { xs: 4, md: 8 }, textAlign: 'center' }}>
        <HeroText variant="h1">
          Plan Your Perfect Trip
        </HeroText>
        <SubText>
          Discover and plan your ideal journey with personalized recommendations and real-time updates.
        </SubText>
      </AnimatedBox>

      <form onSubmit={handleStepSubmit}>
          <StyledCard>
          <CardContent sx={{ 
            p: { xs: 2, sm: 3, md: 4 },
            '&:last-child': { pb: { xs: 2, sm: 3, md: 4 } }
          }}>
            <StyledStepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={step.optional && (
                      <Typography variant="caption" sx={{ 
                        opacity: 0.7,
                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                      }}>
                        Optional
                </Typography>
                    )}
                  >
                    <Typography variant="subtitle1" sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1rem', sm: '1.1rem' }
                    }}>
                      {step.label}
                            </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                      fontSize: { xs: '0.85rem', sm: '0.9rem' }
                    }}>
                      {step.description}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <StepContainer>
                      <Fade in timeout={500}>
                        <Box>
                          {renderStepContent(index)}
                          {error && (
                            <Box sx={{ mt: 2 }}>
                              <Alert 
                                severity="error" 
                                onClose={() => setError(null)}
                      sx={{
                                  borderRadius: '12px',
                                  '& .MuiAlert-icon': {
                                    fontSize: { xs: '20px', sm: '24px' }
                                  }
                                }}
                              >
                                {error}
                              </Alert>
                            </Box>
                          )}
              <Box sx={{ 
                            mt: 3, 
                display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2 
                          }}>
                            <StepNavigationButton
                              disabled={index === 0}
                              onClick={handleBack}
                              sx={{ order: { xs: 2, sm: 1 } }}
                            >
                              Back
                            </StepNavigationButton>
                            <StepNavigationButton
                              variant="contained"
                  type="submit"
                  disabled={loading}
                              sx={{ order: { xs: 1, sm: 2 } }}
                >
                  {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <CircularProgress size={20} color="inherit" />
                          <span>Creating your perfect trip...</span>
                    </Box>
                              ) : index === steps.length - 1 ? (
                    'Generate Trip Plan'
                              ) : (
                                'Continue'
                              )}
                            </StepNavigationButton>
                          </Box>
                        </Box>
                      </Fade>
                    </StepContainer>
                  </StepContent>
                </Step>
              ))}
            </StyledStepper>
            </CardContent>
          </StyledCard>
      </form>

        {tripPlan && (
        <Fade in timeout={800}>
          <Box sx={{ mt: { xs: 3, md: 4 } }}>
            {renderTripPlan()}
          </Box>
          </Fade>
        )}

      <Dialog
        fullWidth
        maxWidth="lg"
        open={mapOpen}
        onClose={handleCloseMap}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 3,
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Trip Map View
          </Typography>
          <IconButton onClick={handleCloseMap} size="large">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '80vh' }}>
          <MapView 
            activities={getActivitiesForMap()} 
            onClose={handleCloseMap}
          />
        </DialogContent>
      </Dialog>

      {isFullyLoaded && budgetAmount > 0 && <BudgetStatus />}
    </StyledContainer>
  );
};

export default TripPlanner;